import { useMemo } from 'react';
import { Placement } from 'react-bootstrap/esm/Overlay';

import TilePercentage, { TilePercentageProps } from '../../../ui/TilePercentage/TilePercentage';
import { calculatePercentage, createRatioFormatter } from '../../../../utils';
import { LearnerFilter } from '../../../admin/GeneralReport/GeneralReport.enum';

import './TilesSummaryReport.scss';

type Props = {
  learnerFilter: LearnerFilter;
  totalLearnerCount: number;
  learnersWhoHaventStartedCount: number;
  learnersWhoHaventCompletedCount: number;
  learnersWhoHaveCompletedCount: number;
  onCopyClick: (filter: LearnerFilter) => void;
  onFilterClick: (filter: LearnerFilter) => void;
  onFilterClear: () => void;
};

const placementRight = 'right' as Placement;

const TilesSummaryReport = ({
  learnerFilter,
  totalLearnerCount,
  learnersWhoHaventStartedCount,
  learnersWhoHaventCompletedCount,
  learnersWhoHaveCompletedCount,
  onCopyClick,
  onFilterClick,
  onFilterClear,
}: Props) => {
  const formatLearnerRatio = createRatioFormatter(totalLearnerCount, 'learners');

  const tiles = [
    {
      active: learnerFilter === LearnerFilter.HaventStarted,
      heading: "Haven't Started",
      tooltipProps: {
        id: 'havent-started-tooltip',
        placement: placementRight,
        text: 'Learners who have not started any course (in the table)',
      },
      percentage: calculatePercentage(learnersWhoHaventStartedCount, totalLearnerCount),
      count: formatLearnerRatio(learnersWhoHaventStartedCount),
      buttonProps: {
        text: 'Filter report',
        onClick: () => onFilterClick(LearnerFilter.HaventStarted),
      },
      removeButtonProps: {
        text: 'Remove filter',
        onClick: onFilterClear,
      },
      copyButtonProps: {
        onClick: () => onCopyClick(LearnerFilter.HaventStarted),
      },
    },
    {
      active: learnerFilter === LearnerFilter.HaventCompleted,
      heading: "Haven't Completed",
      tooltipProps: {
        id: 'havent-completed-tooltip',
        placement: placementRight,
        text: 'Learners who have started a course but have not completed all courses (in the table)',
      },
      percentage: calculatePercentage(learnersWhoHaventCompletedCount, totalLearnerCount),
      count: formatLearnerRatio(learnersWhoHaventCompletedCount),
      buttonProps: {
        text: 'Filter report',
        onClick: () => onFilterClick(LearnerFilter.HaventCompleted),
      },
      removeButtonProps: {
        text: 'Remove filter',
        onClick: onFilterClear,
      },
      copyButtonProps: {
        onClick: () => onCopyClick(LearnerFilter.HaventCompleted),
      },
    },
    {
      active: learnerFilter === LearnerFilter.Completed,
      heading: 'Completed',
      tooltipProps: {
        id: 'completed-tooltip',
        placement: placementRight,
        text: 'Learners who have completed all courses (in the table)',
      },
      percentage: calculatePercentage(learnersWhoHaveCompletedCount, totalLearnerCount),
      count: formatLearnerRatio(learnersWhoHaveCompletedCount),
      buttonProps: {
        text: 'Filter report',
        onClick: () => onFilterClick(LearnerFilter.Completed),
      },
      removeButtonProps: {
        text: 'Remove filter',
        onClick: onFilterClear,
      },
      copyButtonProps: {
        onClick: () => onCopyClick(LearnerFilter.Completed),
      },
    },
  ];

  return (
    <div className="TilesSummaryReport">
      {tiles.map((tile, index) => (
        <TilePercentage key={index} {...tile} />
      ))}
    </div>
  );
};

export default TilesSummaryReport;
